import { SaleLotType } from "constants/saleLots";

import { draftingAttributesMap } from "lib/serializers/draftingAttributes";
import {
  buildDeserializer,
  buildSerializer,
  SerializeMap,
} from "lib/serializerUtils";

import { addressSerializeMap } from "./address";

export const saleLotDefaults = {
  commentIds: [],
  draftingAttributes: null,
  earliestPickupDate: null,
  estimatedAverageMassGrams: 0,
  first_sold: null,
  latestPickupDate: null,
  listingId: "",
  marks: [],
  note: "",
  pickupAddress: null,
  pickupNotes: "",
  quantity: 0,
  quantity_delivered: 0,
  quantityProgeny: 0,
  quantityTagsUsed: 0,
  saleLotType: SaleLotType.AUCTION,
  total_price_cents: 0,
  total_mass_grams: 0,
  thirdPartyId: null,
  youtube_link: "",
};

const draftSightingSerializeMap = {
  lastSeenAtTime: "last_seen_at_time",
  lastSeenAtDraftId: "last_seen_at_draft_id",
};

const clearingSaleAttributesSerializeMap = {
  category: "category",
  comments: "comments",
  hours: "hours",
  inspection: "inspection",
  itemCondition: "item_condition",
  itemType: "item_type",
  make: "make",
  model: "model",
  newUsed: "new_used",
  odometer: "odometer",
  ppsrComments: "ppsr_comments",
  ppsrStatus: "ppsr_status",
  publicDescription: "public_description",
  registration: "registration",
  sellSafeCategory: "sell_safe_category",
  status: "status",
  subType: "sub_type",
  title: "title",
  vin: "vin",
  year: "year",
};

const baseSaleLotSerializeData = {
  accountNote: "account_note",
  age_id: "age_id",
  auction_pen_id: "auction_pen_id",
  bidIncrement: "bid_increment",
  breed_id: "breed_id",
  buyer_id: "buyer_id",
  buyer_way: "buyer_way",
  buyerNumber: "buyer_number",
  buyersPremium: "buyers_premium",
  category_id: "category_id",
  cents_per_head: "cents_per_head",
  clearingSaleAttributes: clearingSaleAttributesSerializeMap,
  commentIds: "comment_ids",
  created: "created",
  deliveryPenId: "delivery_pen_id",
  dentitionId: "dentition_id",
  destination_property_id: "destination_property_id",
  dollar_per_head: "dollar_per_head",
  draftingAttributes: draftingAttributesMap,
  dressingRangeId: "dressing_range_id",
  earliestPickupDate: "earliest_pickup_date",
  estimatedAverageMassGrams: "estimated_average_mass_grams",
  estimatedAverageWeightId: "estimated_average_weight_id",
  exemption_id: "exemption_id",
  exportSites: "export_sites",
  first_sold: "first_sold",
  grade_id: "grade_id",
  id: "id",
  invoiceToBusinessId: "invoice_to_business_id",
  isGSTExempt: "is_gst_exempt",
  is_sold: "is_sold",
  labels: "labels",
  last_modified: "last_modified",
  latestPickupDate: "latest_pickup_date",
  listingFee: "listing_fee",
  listingId: "listing_id",
  lot_number: "lot_number",
  lotNumberSuffix: "lot_number_suffix",
  marks: "marks",
  nlis_sell_file_status: "nlis_sell_file_status",
  note: "note",
  originalListingPrice: "original_listing_price",
  overflowPen: "overflow_pen",
  overflowQuantity: "overflow_quantity",
  parentId: "parent_id",
  permissions: "permissions",
  pickupAddress: addressSerializeMap,
  pickupNotes: "pickup_notes",
  pricing_entries: "pricing_entries",
  quantity: "quantity",
  quantityProgeny: "quantity_progeny",
  quantityTagsUsed: "quantity_tags_used", // Deprecated
  quantity_delivered: "quantity_delivered",
  reservePrice: "reserve_price",
  sale_round: "sale_round",
  saleLotType: "salelot_type",
  sex_id: "sex_id",
  startingBid: "starting_bid",
  subLotNumber: "sub_lot_number",
  thirdPartyId: "third_party_id",
  timeWeighed: "time_weighed",
  total_mass_grams: "total_mass_grams",
  total_price_cents: "total_price_cents",
  transporterId: "transporter_id",
  vendor_id: "vendor_id",
  youtube_link: "youtube_link",
  ...draftSightingSerializeMap,
};

const saleLotSerializeMap: SerializeMap<SaleLot> = {
  ...baseSaleLotSerializeData,
  consignment_id: "consignment_id",
  livestocksale_id: "livestocksale_id",
  pricing_type_id: "pricing_type_id",
  sale_round_id: "sale_round_id",
};

// Define any nested relationships/objects
const nestedObjectMap = {
  drafting_attributes: "draftingAttributes",
  clearing_sale_attributes: "clearingSaleAttributes",
  pickup_address: "pickupAddress",
};

export const serializeSaleLot = buildSerializer(
  saleLotSerializeMap,
  "saleLot",
  nestedObjectMap,
  {},
);

export const deserializeSaleLot = buildDeserializer(
  saleLotSerializeMap,
  "saleLot",
  nestedObjectMap,
  saleLotDefaults,
);

const saleyardScanSaleLotSerializeMap: SerializeMap<SaleyardScanSaleLot> = {
  ...baseSaleLotSerializeData,
  // These fields use camelCase in SaleyardScanSaleLot where SaleLot uses snake_case
  consignmentId: "consignment_id",
  livestockSaleId: "livestocksale_id",
  pricingTypeId: "pricing_type_id",
  saleRoundId: "sale_round_id",
};

export const serializeSaleyardScanSaleLot = buildSerializer(
  saleyardScanSaleLotSerializeMap,
  "saleLot",
  nestedObjectMap,
  {},
);

export const deserializeSaleyardScanSaleLot = buildDeserializer(
  saleyardScanSaleLotSerializeMap,
  "saleLot",
  nestedObjectMap,
  {
    quantity: 0,
    saleLotType: SaleLotType.SALEYARD_SCAN,
  },
);

const bulkUpdateSaleLotSerializeMap = [
  // Must be a subset of the saleLotSerializeMap, otherwise state will be incorrect.
  "age_id",
  "breed_id",
  "buyer_id",
  "buyer_way",
  "category_id",
  "clearingSaleAttributes",
  "deliveryPenId",
  "destination_property_id",
  "dentitionId",
  "draftingAttributes",
  "dressingRangeId",
  "estimatedAverageWeightId",
  "exemption_id",
  "exportSites",
  "id",
  "invoiceToBusinessId",
  "isGSTExempt",
  "note",
  "quantity_delivered",
  "reservePrice",
  "sale_round_id",
  "sex_id",
  "startingBid",
  "thirdPartyId",
  "total_price_cents",
  "transporterId",
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamically building a subset of saleLotSerializeMap
].reduce<Record<string, any>>((acc, key) => {
  const mapKey = key as keyof typeof saleLotSerializeMap;
  if (!saleLotSerializeMap[mapKey]) {
    throw new Error(`Key ${key} is missing from saleLotSerializeMap`);
  }

  acc[key] = saleLotSerializeMap[mapKey];
  return acc;
}, {});

const bulkUpdateNestedObjectMap = {
  clearing_sale_attributes: "clearingSaleAttributes",
  drafting_attributes: "draftingAttributes",
};

export const serializeBulkUpdateSaleLot = buildSerializer(
  bulkUpdateSaleLotSerializeMap,
  "saleLot",
  bulkUpdateNestedObjectMap,
  {},
);

export const deserializeBulkUpdateSaleLot = buildDeserializer(
  bulkUpdateSaleLotSerializeMap,
  "saleLot",
  bulkUpdateNestedObjectMap,
);

export const serializeDraftSighting = buildSerializer(
  draftSightingSerializeMap,
  "draftSighting",
);

export const deserializeDraftSighting = buildDeserializer(
  draftSightingSerializeMap,
  "draftSighting",
);
